/* eslint-disable react/no-unescaped-entities */
"use client";

import { Button, Input, Textarea } from "@resume-studio/ui";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Send, Clock, Sparkles } from "lucide-react";
import React from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Script from "next/script";
import { trackLead, customEvent as trackCustomEvent } from "../../../lib/fpixel";
import { trackGALead, trackGACTAClick, trackGASignUpIntent } from "../../../lib/analytics";

const ContactPageContent = () => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });

  const { executeRecaptcha } = useGoogleReCaptcha();

  const resolveApiBaseUrl = React.useCallback(() => {
    const configuredApiUrl = process.env.NEXT_PUBLIC_API_URL;
    const hasNonLocalhostUrl =
      Boolean(configuredApiUrl) &&
      !configuredApiUrl?.includes("localhost") &&
      !configuredApiUrl?.includes("127.0.0.1");

    if (hasNonLocalhostUrl) {
      return configuredApiUrl as string;
    }

    if (process.env.NODE_ENV === "production") {
      return "https://app.resumestudio.io";
    }

    return configuredApiUrl || "http://localhost:3000";
  }, []);

  // Debug: Check if reCAPTCHA is loaded
  React.useEffect(() => {
    if (executeRecaptcha) {
      console.log("✅ reCAPTCHA v3 is ready");
    }
  }, [executeRecaptcha]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    let recaptchaToken: string | undefined;

    // Execute reCAPTCHA v3 if available
    if (executeRecaptcha) {
      try {
        recaptchaToken = await executeRecaptcha("contact_form");
      } catch (error) {
        console.error("reCAPTCHA error:", error);
      }
    } else {
      console.log("reCAPTCHA not initialized");
    }

    try {
      const response = await fetch(`${resolveApiBaseUrl()}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, recaptchaToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send message");
      }

      setShowSuccess(true);

      // Track contact form submission with Facebook Pixel and Google Analytics
      trackLead({ content_name: "contact_form" });
      trackGALead({ form_name: "contact_form" });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        subject: "",
        message: "",
      });

      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      // Show error in console for now
      console.error("Failed to send message:", error);
      alert(
        error instanceof Error ? error.message : "Failed to send message. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track CTA button clicks
  const handleCTAClick = (ctaName: string, destination: string) => {
    trackCustomEvent("cta_click", { cta_name: ctaName, destination });
    trackGACTAClick({ cta_name: ctaName, cta_location: "contact_page" });
    if (ctaName === "try_builder") {
      trackGASignUpIntent({ source: "contact_page" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/3 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Modern Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4" />
            Let&apos;s Build Something Great Together
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
            Get in{" "}
            <span className="bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
              Touch
            </span>
          </h1>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have questions about our resume building platform? Need help crafting the perfect
            resume? Our team is here to support you every step of the way.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information Cards */}
          <div className="space-y-6">
            {/* Email Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ y: -4 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Mail className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg mb-2">Email Support</h3>
                    <p className="text-gray-600 mb-3">Get in touch via email</p>
                    <p className="text-primary font-medium text-sm mb-3">support@resumestudio.io</p>
                    <a
                      href="mailto:support@resumestudio.io"
                      className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                      Send an email
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Response Time Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -4 }}
              className="group"
            >
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                    <Clock className="w-7 h-7 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg mb-2">Quick Response</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      We typically respond within 2-4 hours during business hours
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-gray-600">
                        <span>Mon - Fri</span>
                        <span className="text-amber-700 font-medium">9 AM - 6 PM GMT</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Saturday</span>
                        <span className="text-amber-700 font-medium">10 AM - 4 PM GMT</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Sunday</span>
                        <span className="text-gray-500">Closed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl p-8 lg:p-10 border border-gray-200 shadow-lg">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Send us a Message</h2>
                <p className="text-gray-600 text-lg">
                  Ready to get started? Fill out the form below and we&apos;ll get back to you
                  within 24 hours.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="space-y-2"
                  >
                    <label className="text-sm font-medium text-gray-700" htmlFor="firstName">
                      First Name
                    </label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="space-y-2"
                  >
                    <label className="text-sm font-medium text-gray-700" htmlFor="lastName">
                      Last Name
                    </label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                    />
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                  className="space-y-2"
                >
                  <label className="text-sm font-medium text-gray-700" htmlFor="email">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="space-y-2"
                >
                  <label className="text-sm font-medium text-gray-700" htmlFor="subject">
                    Subject
                  </label>
                  <Input
                    id="subject"
                    placeholder="How can we help you today?"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-primary focus:ring-primary/20 transition-all duration-200"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                  className="space-y-2"
                >
                  <label className="text-sm font-medium text-gray-700" htmlFor="message">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    placeholder="Tell us more about your needs and how we can help you succeed..."
                    required
                    value={formData.message}
                    onChange={handleChange}
                    minLength={10}
                    className="min-h-[150px] w-full bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-primary focus:ring-primary/20 transition-all duration-200 resize-none"
                  />
                </motion.div>

                {/* reCAPTCHA v3 is invisible - no UI component needed */}

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                >
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-3">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending Message...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-3">
                        Send Message
                        <Send className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Success Alert */}
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.8 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed bottom-6 right-6 z-50"
          >
            <div className="bg-gradient-to-r from-emerald-500 to-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl border border-emerald-400/30 backdrop-blur-sm">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold">Message sent successfully!</p>
                  <p className="text-sm text-emerald-100">We'll get back to you within 24 hours.</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Additional CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-primary/5 to-blue-500/5 rounded-3xl p-8 lg:p-12 border border-primary/20">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Build Your Perfect Resume?
            </h3>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              While you're here, why not try our AI-powered resume builder? Create a professional
              resume in minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`${process.env.NEXT_PUBLIC_APP_URL}/try`}
                onClick={() => handleCTAClick("try_builder", "/try")}
              >
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105">
                  Try Builder Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a
                href="/resume-samples"
                onClick={() => handleCTAClick("browse_templates", "/resume-samples")}
              >
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-primary px-8 py-3 text-lg font-semibold rounded-xl transition-all duration-200"
                >
                  Browse Templates
                </Button>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default function ContactPage() {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  if (!recaptchaSiteKey) {
    console.warn("NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not set");
    return <ContactPageContent />;
  }

  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
        strategy="beforeInteractive"
      />
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        language="en"
        useRecaptchaNet={false}
        useEnterprise={false}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <ContactPageContent />
      </GoogleReCaptchaProvider>
    </>
  );
}
